import type { DmcProfileFull } from '../schemas/organization-dmc-profile';

// ---------------------------------------------------------------------------
// Eleggia DMC Partner Profile — expiry summary (PRD FR-03, read-only slice).
//
// Pure derivation over an already-loaded `DmcProfileFull`: no DB, no I/O, no
// writes. Every time-bound field the spec flags for expiry alerts — insurance
// policies, licenses, representation contracts, documents, and the emergency
// protocol review date — is scanned against a reference "today" and bucketed
// into expired / T-30 / T-60 / ok windows.
//
// The full notification pipeline (email at T-60/T-30, weekly overdue escalation)
// is a later phase; this helper is the single source of truth those alerts and
// the profile UI both read from, so the windows live in one place.
// ---------------------------------------------------------------------------

// Alert windows from PRD FR-03 ("notifies … at T-60 and T-30").
export const DMC_EXPIRY_SOON_DAYS = 30;
export const DMC_EXPIRY_UPCOMING_DAYS = 60;

// A time-bound field that has been evaluated against the reference date.
export type DmcExpiryStatus = 'expired' | 'due-soon' | 'upcoming' | 'ok';

export type DmcExpiryItem = {
  // Which collection the field belongs to (module code family).
  module:
    | 'insurance'
    | 'license'
    | 'representation'
    | 'document'
    | 'protocol-review';
  // Human label for the specific record (falls back to the module name).
  label: string;
  // The expiry / renewal date as a naked UTC date string (`YYYY-MM-DD…`).
  date: string;
  // Whole calendar days from the reference date to `date`. Negative = past.
  daysUntil: number;
  status: DmcExpiryStatus;
};

export type DmcExpirySummary = {
  items: DmcExpiryItem[];
  expiredCount: number;
  dueSoonCount: number;
  upcomingCount: number;
  // Convenience: any item that needs attention (expired OR within T-60).
  attentionCount: number;
};

// UTC-midnight calendar-day diff (reference → target). Positive when the target
// is in the future. Matches the repo's UTC-only date rule — floor both sides to
// their UTC date so a same-day expiry reads as 0, not a fractional day.
const utcDaysUntil = (fromIso: string, toIso: string): number | null => {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const fromMid = Date.UTC(
    from.getUTCFullYear(),
    from.getUTCMonth(),
    from.getUTCDate()
  );
  const toMid = Date.UTC(
    to.getUTCFullYear(),
    to.getUTCMonth(),
    to.getUTCDate()
  );
  return Math.round((toMid - fromMid) / (1000 * 60 * 60 * 24));
};

const statusFor = (daysUntil: number): DmcExpiryStatus => {
  if (daysUntil < 0) return 'expired';
  if (daysUntil <= DMC_EXPIRY_SOON_DAYS) return 'due-soon';
  if (daysUntil <= DMC_EXPIRY_UPCOMING_DAYS) return 'upcoming';
  return 'ok';
};

type ExpirySource = {
  module: DmcExpiryItem['module'];
  label: string;
  date: string | null;
};

// Compute the expiry summary from a loaded profile. `referenceIso` defaults to
// nothing on purpose — the caller passes "now" so the function stays pure and
// deterministic (no `Date.now()` inside), matching the repo's testability rule.
export const summarizeDmcExpiries = (
  profile: DmcProfileFull,
  referenceIso: string
): DmcExpirySummary => {
  const sources: ExpirySource[] = [
    ...profile.insurancePolicies.map((p) => ({
      module: 'insurance' as const,
      label: p.policyType ?? p.insurer ?? 'Insurance policy',
      date: p.expiryDate,
    })),
    ...profile.licenses.map((l) => ({
      module: 'license' as const,
      label: l.name ?? l.type ?? 'License',
      date: l.validUntil,
    })),
    ...profile.representations.map((r) => ({
      module: 'representation' as const,
      label: r.firmName ?? 'Representation contract',
      date: r.contractEnd,
    })),
    ...profile.documents.map((d) => ({
      module: 'document' as const,
      label: d.title ?? d.category ?? 'Document',
      date: d.expiryDate,
    })),
    {
      module: 'protocol-review' as const,
      label: 'Emergency protocol review',
      date: profile.protocolReviewDate,
    },
  ];

  const items: DmcExpiryItem[] = [];
  for (const source of sources) {
    if (!source.date) continue;
    const daysUntil = utcDaysUntil(referenceIso, source.date);
    if (daysUntil === null) continue;
    items.push({
      module: source.module,
      label: source.label,
      date: source.date,
      daysUntil,
      status: statusFor(daysUntil),
    });
  }

  // Soonest-expiring first so the UI leads with the most urgent row.
  items.sort((a, b) => a.daysUntil - b.daysUntil);

  const expiredCount = items.filter((i) => i.status === 'expired').length;
  const dueSoonCount = items.filter((i) => i.status === 'due-soon').length;
  const upcomingCount = items.filter((i) => i.status === 'upcoming').length;

  return {
    items,
    expiredCount,
    dueSoonCount,
    upcomingCount,
    attentionCount: expiredCount + dueSoonCount + upcomingCount,
  };
};
